/**
 * MiniFlowCanvas Component
 * WP-DASH-2: SVG container for mini Sankey visualization
 *
 * Handles responsive sizing and coordinates the layout of nodes and edges.
 * Includes column labels and "more" indicators.
 */

import { memo, useRef, useState, useEffect, useMemo } from 'react';
import type { InflowNode, AccountNode, OutflowNode, FlowEdge } from '@/components/flow/types';
import { MiniNodeColumn, MiniMoreIndicator } from './MiniNode';
import { MiniEdgeLayer } from './MiniEdgeLayer';
import {
  computeMiniLayout,
  MINI_CANVAS,
  MAX_NODES_PER_COLUMN,
  type CanvasDimensions,
} from './miniSankeyUtils';

// ============================================================================
// Types
// ============================================================================

interface MiniFlowCanvasProps {
  inflowNodes: InflowNode[];
  accountNodes: AccountNode[];
  outflowNodes: OutflowNode[];
  edges: FlowEdge[];
}

// ============================================================================
// Component
// ============================================================================

/**
 * MiniFlowCanvas - SVG canvas for mini Sankey diagram
 *
 * Features:
 * - Responsive width based on container
 * - Column labels (Inflows, Accounts, Outflows)
 * - Edges rendered under nodes
 * - "+N more" indicators when nodes exceed limit
 */
function MiniFlowCanvasComponent({
  inflowNodes,
  accountNodes,
  outflowNodes,
  edges,
}: MiniFlowCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState<CanvasDimensions>(MINI_CANVAS);

  // Responsive sizing
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      const { width } = entry.contentRect;
      setDimensions((prev) => ({
        ...prev,
        width: Math.max(width, 320), // Minimum width
      }));
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Limit nodes to top N per column
  const topInflowNodes = useMemo(
    () =>
      [...inflowNodes]
        .sort((a, b) => b.value - a.value)
        .slice(0, MAX_NODES_PER_COLUMN),
    [inflowNodes]
  );

  const topAccountNodes = useMemo(
    () =>
      [...accountNodes]
        .sort((a, b) => a.flowTotal - b.flowTotal)  // Ascending: smallest first (checking accounts)
        .slice(0, MAX_NODES_PER_COLUMN),
    [accountNodes]
  );

  const topOutflowNodes = useMemo(
    () =>
      [...outflowNodes]
        .sort((a, b) => b.value - a.value)
        .slice(0, MAX_NODES_PER_COLUMN),
    [outflowNodes]
  );

  // Compute layout
  const layout = useMemo(
    () => computeMiniLayout(topInflowNodes, topAccountNodes, topOutflowNodes, dimensions),
    [topInflowNodes, topAccountNodes, topOutflowNodes, dimensions]
  );

  // Filter edges to only those connecting visible nodes
  // Map node IDs from full nodes to positioned nodes
  const visibleNodeIds = useMemo(() => {
    const ids = new Set<string>();
    for (const node of [...topInflowNodes, ...topAccountNodes, ...topOutflowNodes]) {
      ids.add(node.id);
    }
    return ids;
  }, [topInflowNodes, topAccountNodes, topOutflowNodes]);

  const visibleEdges = useMemo(
    () => edges.filter((e) => visibleNodeIds.has(e.source) && visibleNodeIds.has(e.target)),
    [edges, visibleNodeIds]
  );

  // Count hidden nodes
  const hiddenInflowCount = inflowNodes.length - topInflowNodes.length;
  const hiddenAccountCount = accountNodes.length - topAccountNodes.length;
  const hiddenOutflowCount = outflowNodes.length - topOutflowNodes.length;

  // Column label Y position
  const labelY = 16;

  return (
    <div ref={containerRef} className="w-full overflow-hidden">
      <svg
        viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
        className="w-full h-auto"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Money flow diagram showing income sources, accounts, and expenses"
      >
        {/* Column labels */}
        <text
          x={layout.inflowX + layout.columnWidth / 2}
          y={labelY}
          textAnchor="middle"
          className="text-[11px] font-medium"
          fill="#6B7280"
        >
          Inflows
        </text>
        <text
          x={layout.accountX + layout.columnWidth / 2}
          y={labelY}
          textAnchor="middle"
          className="text-[11px] font-medium"
          fill="#6B7280"
        >
          Accounts
        </text>
        <text
          x={layout.outflowX + layout.columnWidth / 2}
          y={labelY}
          textAnchor="middle"
          className="text-[11px] font-medium"
          fill="#6B7280"
        >
          Outflows
        </text>

        {/* Edges (render first, under nodes) */}
        <MiniEdgeLayer edges={visibleEdges} nodePositions={layout.nodePositions} />

        {/* Nodes */}
        <MiniNodeColumn nodes={layout.inflowNodes} type="inflow" />
        <MiniNodeColumn nodes={layout.accountNodes} type="account" />
        <MiniNodeColumn nodes={layout.outflowNodes} type="outflow" />

        {/* "More" indicators */}
        {hiddenInflowCount > 0 && (
          <MiniMoreIndicator
            x={layout.inflowX}
            y={dimensions.height - 8}
            count={hiddenInflowCount}
            type="inflow"
          />
        )}
        {hiddenAccountCount > 0 && (
          <MiniMoreIndicator
            x={layout.accountX}
            y={dimensions.height - 8}
            count={hiddenAccountCount}
            type="account"
          />
        )}
        {hiddenOutflowCount > 0 && (
          <MiniMoreIndicator
            x={layout.outflowX}
            y={dimensions.height - 8}
            count={hiddenOutflowCount}
            type="outflow"
          />
        )}
      </svg>
    </div>
  );
}

export const MiniFlowCanvas = memo(MiniFlowCanvasComponent);

export default MiniFlowCanvas;
